'use client';

import React, { useState } from 'react';
import Modal from '@/components/ui/Modal';
import StatusBadge from '@/components/ui/StatusBadge';
import { FlockRecord } from './flockData';
import { Bird, Wheat, Scale, TrendingUp, AlertTriangle,  } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


interface Props {
  flock: FlockRecord;
  open: boolean;
  onClose: () => void;
}

const TABS = [
  { id: 'tab-overview', label: 'Overview' },
  { id: 'tab-daily', label: 'Daily Records' },
  { id: 'tab-feed', label: 'Feed' },
  { id: 'tab-health', label: 'Health' },
  { id: 'tab-mortality', label: 'Mortality' },
];

// Backend: GET /api/flocks/:id/daily-records
const DAILY_RECORDS = [
  { id: 'dr-001', date: '03/09/2026', opening: 4632, mortality: 18, culled: 0, closing: 4614, feedIssued: 420, feedConsumed: 418, water: 840, eggs: 0, recorder: 'Ogenga Paul' },
  { id: 'dr-002', date: '02/09/2026', opening: 4648, mortality: 16, culled: 0, closing: 4632, feedIssued: 418, feedConsumed: 416, water: 836, eggs: 0, recorder: 'Ogenga Paul' },
  { id: 'dr-003', date: '01/09/2026', opening: 4660, mortality: 12, culled: 0, closing: 4648, feedIssued: 415, feedConsumed: 413, water: 830, eggs: 0, recorder: 'Apio Christine' },
  { id: 'dr-004', date: '31/08/2026', opening: 4671, mortality: 11, culled: 0, closing: 4660, feedIssued: 412, feedConsumed: 410, water: 824, eggs: 0, recorder: 'Ogenga Paul' },
  { id: 'dr-005', date: '30/08/2026', opening: 4682, mortality: 11, culled: 0, closing: 4671, feedIssued: 410, feedConsumed: 408, water: 820, eggs: 0, recorder: 'Okello James' },
];

// Backend: GET /api/flocks/:id/mortality
const MORTALITY_RECORDS = [
  { id: 'mr-001', date: '03/09/2026', count: 18, cause: 'Respiratory infection suspected', disposal: 'Burial pit', notes: 'Cluster in south corner of house', recorder: 'Ogenga Paul' },
  { id: 'mr-002', date: '02/09/2026', count: 16, cause: 'Respiratory infection', disposal: 'Burial pit', notes: 'Continuing pattern — vet notified', recorder: 'Ogenga Paul' },
  { id: 'mr-003', date: '01/09/2026', count: 12, cause: 'Unknown', disposal: 'Burial pit', notes: 'No obvious symptoms observed', recorder: 'Apio Christine' },
  { id: 'mr-004', date: '31/08/2026', count: 11, cause: 'Heat stress', disposal: 'Burial pit', notes: 'Temperature peaked at 34°C', recorder: 'Ogenga Paul' },
];

// Backend: GET /api/flocks/:id/health
const HEALTH_RECORDS = [
  { id: 'hr-001', date: '03/09/2026', type: 'Treatment', condition: 'Respiratory Infection', medication: 'Enrofloxacin 10% + Vitamin C', dosage: '1ml/L water', duration: '5 days', vet: 'Dr. Nakato Sarah', cost: 180000 },
  { id: 'hr-002', date: '28/08/2026', type: 'Observation', condition: 'Mild lethargy in 20 birds', medication: 'Vitamin supplement', dosage: '2ml/L water', duration: '3 days', vet: 'Okello James', cost: 45000 },
];

function OverviewTab({ flock }: { flock: FlockRecord }) {
  const profitIndicator = flock.fcr <= 1.8 ? 'On target' : flock.fcr <= 2.1 ? 'Slightly above target' : 'Above target — review feed';
  const profitColor = flock.fcr <= 1.8 ? 'var(--success)' : flock.fcr <= 2.1 ? 'var(--warning)' : 'var(--danger)';

  return (
    <div className="space-y-5">
      {/* Key metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { id: 'fm-birds', icon: Bird, label: 'Current Birds', value: flock.currentQty.toLocaleString(), sub: `of ${flock.initialQty.toLocaleString()} initial`, color: 'var(--primary)' },
          { id: 'fm-survival', icon: TrendingUp, label: 'Survival Rate', value: `${flock.survivalRate.toFixed(1)}%`, sub: `${flock.mortalityPct.toFixed(2)}% mortality`, color: flock.survivalRate >= 97 ? 'var(--success)' : 'var(--warning)' },
          { id: 'fm-weight', icon: Scale, label: 'Avg Weight', value: flock.avgWeightKg > 0 ? `${flock.avgWeightKg.toFixed(2)} kg` : '—', sub: `Age: ${flock.ageDays} days`, color: 'var(--foreground)' },
          { id: 'fm-fcr', icon: Wheat, label: 'Feed Conv. Ratio', value: flock.fcr > 0 ? flock.fcr.toFixed(2) : '—', sub: profitIndicator, color: profitColor },
        ].map((m) => {
          const Icon = m.icon;
          return (
            <div key={m.id} className="kpi-card">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={14} style={{ color: m.color }} />
                <span className="text-xs text-muted-foreground font-medium">{m.label}</span>
              </div>
              <div className="text-xl font-bold font-tabular" style={{ color: m.color }}>{m.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{m.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Flock info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Flock Details</h4>
          {[
            { id: 'fd-num', label: 'Flock Number', value: flock.flockNumber },
            { id: 'fd-name', label: 'Flock Name', value: flock.flockName },
            { id: 'fd-type', label: 'Bird Type', value: flock.birdType },
            { id: 'fd-breed', label: 'Breed', value: flock.breed },
            { id: 'fd-purpose', label: 'Purpose', value: flock.purpose },
            { id: 'fd-supplier', label: 'Supplier / Hatchery', value: flock.supplier },
          ].map((row) => (
            <div key={row.id} className="flex justify-between py-1.5 border-b" style={{ borderColor: 'var(--border)' }}>
              <span className="text-xs text-muted-foreground">{row.label}</span>
              <span className="text-xs font-medium text-foreground text-right">{row.value}</span>
            </div>
          ))}
        </div>
        <div className="space-y-1">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Location & Dates</h4>
          {[
            { id: 'fl-farm', label: 'Farm', value: flock.farm },
            { id: 'fl-house', label: 'Poultry House', value: flock.house },
            { id: 'fl-arrival', label: 'Arrival Date', value: flock.arrivalDate },
            { id: 'fl-target', label: 'Target Market Date', value: flock.targetMarketDate },
            { id: 'fl-cycle', label: 'Expected Cycle', value: `${flock.expectedCycleDays} days` },
            { id: 'fl-cost', label: 'Total Acquisition Cost', value: `UGX ${flock.totalAcquisitionCost.toLocaleString()}` },
          ].map((row) => (
            <div key={row.id} className="flex justify-between py-1.5 border-b" style={{ borderColor: 'var(--border)' }}>
              <span className="text-xs text-muted-foreground">{row.label}</span>
              <span className="text-xs font-medium text-foreground text-right">{row.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Mortality alert if high */}
      {flock.mortalityPct > 3 && (
        <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ backgroundColor: 'var(--danger-bg)', borderColor: '#fecaca' }}>
          <AlertTriangle size={15} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <p className="text-xs font-semibold" style={{ color: 'var(--danger)' }}>Elevated Mortality Alert</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Cumulative mortality of {flock.mortalityPct.toFixed(2)}% exceeds the 3% farm threshold. Review health records and consider veterinary consultation.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function DailyRecordsTab() {
  return (
    <div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ minWidth: '700px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              {['Date', 'Opening', 'Mortality', 'Culled', 'Closing', 'Feed Issued (kg)', 'Feed Consumed', 'Water (L)', 'Recorded By'].map((h) => (
                <th key={`dh-${h}`} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground" style={{ letterSpacing: '0.02em' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {DAILY_RECORDS.map((r) => (
              <tr key={r.id} className="table-row-hover border-b" style={{ borderColor: 'var(--border)' }}>
                <td className="px-3 py-2 text-xs font-tabular text-foreground">{r.date}</td>
                <td className="px-3 py-2 text-xs font-tabular text-foreground">{r.opening.toLocaleString()}</td>
                <td className="px-3 py-2 text-xs font-tabular font-semibold" style={{ color: r.mortality > 15 ? 'var(--danger)' : 'var(--warning)' }}>{r.mortality}</td>
                <td className="px-3 py-2 text-xs font-tabular text-muted-foreground">{r.culled}</td>
                <td className="px-3 py-2 text-xs font-tabular font-semibold text-foreground">{r.closing.toLocaleString()}</td>
                <td className="px-3 py-2 text-xs font-tabular text-foreground">{r.feedIssued}</td>
                <td className="px-3 py-2 text-xs font-tabular text-foreground">{r.feedConsumed}</td>
                <td className="px-3 py-2 text-xs font-tabular text-foreground">{r.water}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.recorder}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MortalityTab() {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-muted-foreground">Showing last 30 days of mortality records</div>
        <div className="text-xs font-semibold" style={{ color: 'var(--danger)' }}>
          Total: {MORTALITY_RECORDS.reduce((s, m) => s + m.count, 0)} birds
        </div>
      </div>
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ minWidth: '600px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              {['Date', 'Count', 'Suspected Cause', 'Disposal', 'Notes', 'Recorded By'].map((h) => (
                <th key={`mh-${h}`} className="px-3 py-2 text-left text-xs font-semibold text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {MORTALITY_RECORDS.map((r) => (
              <tr key={r.id} className="table-row-hover border-b" style={{ borderColor: 'var(--border)' }}>
                <td className="px-3 py-2 text-xs font-tabular text-foreground">{r.date}</td>
                <td className="px-3 py-2 text-xs font-bold font-tabular" style={{ color: 'var(--danger)' }}>{r.count}</td>
                <td className="px-3 py-2 text-xs text-foreground">{r.cause}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.disposal}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground max-w-xs truncate">{r.notes}</td>
                <td className="px-3 py-2 text-xs text-muted-foreground">{r.recorder}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function HealthTab() {
  return (
    <div className="space-y-3">
      {HEALTH_RECORDS.map((r) => (
        <div key={r.id} className="p-4 rounded-lg border" style={{ borderColor: 'var(--border)' }}>
          <div className="flex items-start justify-between gap-3 mb-2">
            <div>
              <span className="text-xs font-semibold text-foreground">{r.condition}</span>
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: r.type === 'Treatment' ? 'var(--danger-bg)' : 'var(--info-bg)', color: r.type === 'Treatment' ? 'var(--danger)' : 'var(--info)' }}>
                {r.type}
              </span>
            </div>
            <span className="text-xs text-muted-foreground font-tabular flex-shrink-0">{r.date}</span>
          </div>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            {[
              { id: `hr-med-${r.id}`, l: 'Medication', v: r.medication },
              { id: `hr-dos-${r.id}`, l: 'Dosage', v: r.dosage },
              { id: `hr-dur-${r.id}`, l: 'Duration', v: r.duration },
              { id: `hr-vet-${r.id}`, l: 'Prescribed By', v: r.vet },
              { id: `hr-cost-${r.id}`, l: 'Cost', v: `UGX ${r.cost.toLocaleString()}` },
            ].map((item) => (
              <div key={item.id} className="flex gap-2">
                <span className="text-xs text-muted-foreground min-w-[80px]">{item.l}:</span>
                <span className="text-xs font-medium text-foreground">{item.v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function FlockDetailModal({ flock, open, onClose }: Props) {
  const [activeTab, setActiveTab] = useState('tab-overview');

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${flock.flockNumber} — ${flock.flockName}`}
      subtitle={`${flock.birdType} · ${flock.breed} · ${flock.house} · ${flock.farm}`}
      size="xl"
    >
      {/* Status row */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        <StatusBadge status={flock.status} dot />
        <span className="text-xs text-muted-foreground">Age: <strong className="text-foreground">{flock.ageDays} days</strong></span>
        <span className="text-xs text-muted-foreground">Birds: <strong className="text-foreground">{flock.currentQty.toLocaleString()}</strong></span>
        <span className="text-xs text-muted-foreground">Arrival: <strong className="text-foreground">{flock.arrivalDate}</strong></span>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px"
            style={{
              borderBottomColor: activeTab === tab.id ? 'var(--primary)' : 'transparent',
              color: activeTab === tab.id ? 'var(--primary)' : 'var(--muted-foreground)',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="fade-in">
        {activeTab === 'tab-overview' && <OverviewTab flock={flock} />}
        {activeTab === 'tab-daily' && <DailyRecordsTab />}
        {activeTab === 'tab-feed' && (
          <div className="text-sm text-muted-foreground py-8 text-center">
            Feed consumption records for {flock.flockNumber} — connect to Feed Management module.
          </div>
        )}
        {activeTab === 'tab-health' && <HealthTab />}
        {activeTab === 'tab-mortality' && <MortalityTab />}
      </div>
    </Modal>
  );
}